import { useState } from "react";
import { Plus, Briefcase } from "lucide-react";
import type { ModelWithFilesAndLinks } from "@maker-toolchest/shared";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { Pagination } from "@/components/Pagination";
import { JobTable } from "@/features/jobs/components/JobTable";
import { useJobs } from "@/features/jobs/hooks";
import { useUIStore } from "@/stores/ui";
import { useNavigate } from "react-router-dom";

interface ModelJobsTabProps {
  model: ModelWithFilesAndLinks;
}

export function ModelJobsTab({ model }: ModelJobsTabProps) {
  const [jobsPage, setJobsPage] = useState(1);
  const navigate = useNavigate();
  const { openAddJobSidebar } = useUIStore();

  // Fetch jobs associated with this model
  const { data: jobsData, isLoading: jobsLoading } = useJobs({
    modelId: model.id,
    page: jobsPage,
    limit: 25,
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground">Associated Jobs</h3>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => openAddJobSidebar({ defaultModelId: model.id })}
        >
          Add Job
        </Button>
      </div>

      {jobsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : jobsData?.data && jobsData.data.length > 0 ? (
        <>
          <JobTable
            jobs={jobsData.data}
            onAddTasks={(job) =>
              navigate(`/projects/jobs/${job.id}?action=addTasks`)
            }
            onUpdateStatus={(job) =>
              navigate(`/projects/jobs/${job.id}?action=updateStatus`)
            }
            onViewTasks={(job) => navigate(`/projects/jobs/${job.id}`)}
            onCancelJob={(job) =>
              navigate(`/projects/jobs/${job.id}?action=cancel`)
            }
          />
          {jobsData.meta?.pagination && (
            <Pagination
              page={jobsData.meta.pagination.page}
              total={jobsData.meta.pagination.total}
              limit={jobsData.meta.pagination.limit}
              onPageChange={setJobsPage}
            />
          )}
        </>
      ) : (
        <EmptyState
          icon={<Briefcase className="h-10 w-10" />}
          title="No jobs yet"
          description="Create a job using this model to track your work"
        />
      )}
    </div>
  );
}
